/**
 * Color utilities — WCAG luminance math shared by the DesignSpec compiler
 * (choosing readable label colors) and the Quality Validation Engine
 * (judging them). One implementation so the chooser and the judge can
 * never disagree.
 */

export const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return null;
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
};

const toHex = ({ r, g, b }) =>
  '#' + [r, g, b].map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0')).join('');

/** WCAG relative luminance (0–1). Returns null for unparseable colors. */
export const relativeLuminance = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const lin = (v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(rgb.r) + 0.7152 * lin(rgb.g) + 0.0722 * lin(rgb.b);
};

/** WCAG contrast ratio (1–21). Returns null when either color is unparseable. */
export const contrastRatio = (a, b) => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (la === null || lb === null) return null;
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
};

/** Composite a hex color at `alpha` over a hex background. */
export const blendOver = (fg, alpha, bg) => {
  const f = hexToRgb(fg);
  const b = hexToRgb(bg);
  if (!f || !b) return bg;
  return toHex({
    r: f.r * alpha + b.r * (1 - alpha),
    g: f.g * alpha + b.g * (1 - alpha),
    b: f.b * alpha + b.b * (1 - alpha),
  });
};

/** The candidate with the highest contrast against `surface`. Deterministic. */
export const mostReadableOn = (surface, candidates) =>
  candidates.reduce((best, candidate) =>
    (contrastRatio(candidate, surface) || 0) > (contrastRatio(best, surface) || 0) ? candidate : best
  );
